import { serverTime } from 'scbk-services';
import { formatUtils } from '.';

/**
 * 현재 서버 시간 반환.
 * - 서버와의 시간차를 계산해서 내려준다.
 * @returns Date
 */
export const getSystemDate = (): Date => {
  return serverTime.now();
};

/**
 * 시스템 시간을 time 형식으로 반환.
 *
 * @returns
 */
export const getSystemTime = (): string => {
  return toTimeString(getSystemDate());
};

/**
 *
 * @returns
 */
export const getSystemTimeMillis = () => {
  return serverTime.nowMillis();
};

/**
 * 년도 반환.
 *
 * @returns
 */
export const getYear = (): string => {
  return `${getSystemDate().getFullYear()}`;
};

/**
 * 월 반환.
 *
 * @returns
 */
export const getMonth = (): string => {
  var month = getSystemDate().getMonth() + 1;
  return month < 10 ? `0${month}` : `${month}`;
};

/**
 * 일 반환.
 *
 * @returns
 */
export const getDay = (): string => {
  var day = getSystemDate().getDate();
  return day < 10 ? `0${day}` : `${day}`;
};

/**
 * 시간 반환.
 *
 * @returns
 */
export const getHour = (): string => {
  var hour = getSystemDate().getHours();
  return hour < 10 ? `0${hour}` : `${hour}`;
};

/**
 * 분 반환.
 */
export const getMin = (): string => {
  var min = getSystemDate().getMinutes();
  return min < 10 ? `0${min}` : `${min}`;
};

/**
 * 초 반환.
 */
export const getSec = (): string => {
  var sec = getSystemDate().getSeconds();
  return sec < 10 ? `0${sec}` : `${sec}`;
};

/**
 * 주말여부 반환
 * @param d
 * @returns
 */
export const isWeekend = (d: string = getToday()): boolean => {
  const year = Number(d.substring(0, 4));
  const month = Number(d.substring(4, 6)) - 1;
  const date = Number(d.substring(6, 8));
  return [0, 6].includes(new Date(year, month, date).getDay());
};

/**
 * YYYY + char + MM + char + DD 로 오늘 날짜 반환
 * @param separator 구분자 (기본값 '.')
 * @return today EX) 2009.02.12
 */
export const getFormatToday = (separator: string = '.'): string => {
  return `${getYear()}${separator}${getMonth()}${separator}${getDay()}`;
};

/**
 * YYYYMMDD 형식 오늘 날자 반환.
 *
 * @return
 */
export const getToday = () => {
  return `${getYear()}${getMonth()}${getDay()}`;
};

/**
 * 날짜 문자열을 구분자로 포매팅하여 반환
 * @param str
 * @param separator
 * @returns
 */
export const getFrmDate = (str: string, separator: string = '.') => {
  if (typeof str == 'undefined' || str == '') {
    str = getToday();
  }

  return formatUtils.setDelim(str, '9999' + separator + '99' + separator + '99');
};

/**
 * 문자 형식 날자를 Date 형식으로 변환.
 * @param d yyyyMMdd 형식의 날짜 문자열
 * @returns Date 객체
 */
export const getStrToDate = (d: string): Date | null => {
  const s: string = formatUtils.toNumber(d);
  if (!s || s.length < 8) return null;

  const year = Number(s.slice(0, 4));
  const month = Number(s.slice(4, 6));
  const day = Number(s.slice(6, 8));

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
};

/**
 * 해당 날자가 속한 월요일 날자 반환.
 * @param src
 * @returns
 */
export const getMonday = (src: string): Date => {
  const d = src ? new Date(src) : new Date();
  var day = d.getDay();
  var diff = d.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
  return new Date(d.setDate(diff));
};

/**
 * 해당 날자가 속한 금요일 날자 반환.
 */
export const getFriday = (src: string) => {
  const d = src ? new Date(src) : new Date();
  var day = d.getDay();
  var diff = d.getDate() - day + (day == 0 ? -6 : 5); // adjust when day is sunday
  return new Date(d.setDate(diff));
};

/**
 * 두 일자 사이의 일수를 반환.
 * @param date1 문자열 데이터 '20041012' 형식
 * @param date2 문자열 데이터 '20041103' 형식
 * @return 두 일자 사이의 일수
 */
export const getDaysBetween = (date1: string, date2: string): number => {
  const a = getStrToDate(date1);
  const b = getStrToDate(date2);
  if (!a || !b) return 0;

  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((b.getTime() - a.getTime()) / msPerDay);
};

/**
 * 자바 스크립트 Date 객체를 Time 스트링으로 반환.
 *
 * @param date: JavaScript Date Object
 * @return
 */
export const toTimeString = (date: Date): string => {
  const pad2 = (n: number) => String(n).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const hour = pad2(date.getHours());
  const min = pad2(date.getMinutes());
  const sec = pad2(date.getSeconds());

  return `${year}${month}${day}${hour}${min}${sec}`;
};

/**
 *
 * @param year
 * @param month
 * @param day
 * @returns
 */
const partsToYmd = (year: number, month: number, day: number): string => {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  return `${year}${pad2(month)}${pad2(day)}`;
};

/**
 * 월을 더한 날짜 반환.
 *
 * @param (String) years
 * @return (String) yyyyMMdd
 */
export const getAddMonths = (months: number, date?: string): string => {
  const before = date ? date : getToday();

  const y = Number(before.slice(0, 4));
  const m = Number(before.slice(4, 6));
  const d = Number(before.slice(6, 8));

  const add = Number(months) || 0;

  const moved = new Date(y, m - 1 + add, 1);
  const year = moved.getFullYear();
  const month = moved.getMonth() + 1;

  const last = getLastDay(year, month);
  const day = Math.min(d, last);
  return partsToYmd(year, month, day);
};

/**
 * 년도를 더한 날짜 반환.
 * @param years 년도
 * @param date
 * @returns
 */
export const getAddYears = (years: number, date: string = ''): string => {
  const y = Number(years) || 0;
  return getAddMonths(y * 12, date);
};

/**
 * 일자를 더한 날짜 반환
 * @param days 일자
 * @param date
 * @returns
 */
export const getAddDays = (days: number, date?: string) => {
  const before = date ? date : getToday();

  const y = Number(before.slice(0, 4));
  const m = Number(before.slice(4, 6));
  const d = Number(before.slice(6, 8));

  const add = Number(days) || 0;

  const dt = new Date(y, m - 1, d, 12, 0, 0, 0);
  dt.setDate(dt.getDate() + add);

  return partsToYmd(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
};

/**
 * 월말 일자 반환.
 * @param year
 * @param mon
 * @returns
 */
export const getLastDay = (year: number, mon: number): number => {
  const month = mon - 1;
  const d = new Array(31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31);
  if (0 == year % 4 && (0 != year % 100 || 0 == year % 400) && month == 1) {
    return 29;
  } else {
    return d[month];
  }
};

/**
 * 현재 월의 첫 날짜 반환.
 * @param delimiter 구분자 (기본값: 공백(''))
 * @returns
 */
export const getMonthFirstDay = (delimiter: string = ''): string => {
  const d = getSystemDate();

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = '01';

  return `${year}${delimiter}${month}${delimiter}${day}`;
};

/**
 * 현재 월의 마지막 일자 반환.
 * @param delimiter 구분자 (기본값: 공백(''))
 * @returns
 */
export const getMonthLastDay = (delimiter: string = '') => {
  const d = getSystemDate();
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();

  return `${year}${delimiter}${String(month).padStart(2, '0')}${delimiter}${String(lastDay).padStart(2, '0')}`;
};

/**
 * 날짜 설정
 * @param df
 * @returns
 */
export const setDate = (df: string): string => {
  const isMinus = df.indexOf('-') > -1 ? -1 : 1;
  const addVal = Number(formatUtils.toNumber(df)) * isMinus;
  if (df === 'today') {
    return getToday();
  }

  if (df.indexOf('addY') > -1) {
    return getAddYears(addVal);
  }

  if (df.indexOf('addM') > -1) {
    return getAddMonths(addVal);
  }

  if (df.indexOf('addW') > -1) {
    return getAddDays(addVal * 7);
  }

  if (df.indexOf('addD') > -1) {
    return getAddDays(addVal);
  }
  return df;
};
